import type {
  AccessRequestOf,
  ArgsOf,
  RequestOf,
  StagedAccessRequestOf,
  StagedRequestOf,
} from "@p0security/shared/types/access/index";
import { Access, Delegate } from "@p0security/shared/types/access/index";

import { GcloudServices } from "./resource/constants.ts";

const projectArg = {
  description: "The requested Google Cloud project",
  label: "Project",
  type: "static",
  optional: true,
  isPreselect: true,
  waitForInput: true,
} as const;

const projectIdArg = {
  type: "generated",
  label: "Project ID",
} as const;

const roleNameArg = {
  description: "Role name or identifier",
  label: "Role",
  type: "dynamic",
  multivalued: true,
} as const;

const principalTypeArg = {
  description: "The type of principal that should receive access",
  label: "Principal type",
  type: "input",
  choices: ["user", "group", "service-account"],
  supportedCommands: ["grant"],
  optional: true,
} as const;

// The real "role" access's parameters, reused below both by the real access
// (in GcloudAccesses) and by the toolUser delegate's type-only stand-in --
// this keeps the delegate's `args` accurately typed against role's real
// input fields without a circular reference to GcloudAccesses itself (which
// is still being defined at the point the delegate needs to reference it).
const roleParameters = {
  project: projectArg,
  projectId: projectIdArg,
  "principal-type": principalTypeArg,
  name: roleNameArg,
  role: {
    type: "generated",
    label: "Role ID",
  },
  nIncludedPermissions: {
    type: "generated",
    label: "Number of included permissions",
    optional: true,
  },
} as const;

// Type-only stand-in for gcloud's own "role" access, matching its real
// parameters exactly (see roleParameters above) but never actually used at
// runtime -- delegatedProviderFor (packages/core/src/lifecycle/delegation.ts)
// only reads `delegate.resource` to look up the real "gcloud" resource
// fresh, so this only exists so TypeScript can type-check the toolUser
// delegate's `args` against role's real fields.
const roleAccessShape = Access({
  description: "Role within Google Cloud",
  label: "Role",
  maturity: "ga",
  delegation: {},
  generated: {},
  parameters: roleParameters,
  render: { locator: "projectId", policy: "role" } as const,
});

// Delegates a `roles/mcp.toolUser` grant on gcloud's own "role" access type --
// every agentic gcloud request needs this baseline role bound to the caller's
// WIF principal for the calling tool to function, in addition to whatever
// specific access was requested.
const toolUserDelegation = {
  toolUser: Delegate({
    resource: "gcloud" as const,
    accesses: { role: roleAccessShape },
    key: "role" as const,
  }),
};

export const GcloudAccesses = {
  resource: Access({
    description: "Resource within Google Cloud",
    label: "Resource",
    maturity: "ga",
    delegation: toolUserDelegation,
    generated: {
      roles: {
        type: "object",
        multivalued: true,
        properties: {
          role: { type: "string" },
          permissionId: { type: "string" },
        },
      },
    },
    parameters: {
      project: projectArg,
      projectId: projectIdArg,
      "principal-type": principalTypeArg,
      locator: {
        type: "dynamic",
        label: "Resource locator",
        description: "Resource Locator",
        hint: "Supported resources: Compute instances & zones, Storage buckets, BigQuery tables, service accounts",
      },
      // Human-readable name of the requested resource. The locator itself is
      // machine-readable (a JSON-encoded selection for non-compute/storage
      // services), so requests are rendered from this field instead.
      resourceName: {
        type: "generated",
        label: "Resource",
        optional: true,
      },
      accesses: {
        type: "dynamic",
        label: "Access",
        description: "admin, read, write or role name",
        multivalued: true,
      },
      serviceAccount: {
        type: "input",
        label: "Service account",
        description: "Service Account",
        hint: "Find with: gcloud compute instances describe INSTANCE-NAME | grep serviceAccounts -A 1",
        optional: true,
      },
      object: {
        type: "input",
        label: "File or folder",
        description: "Path to file or folder inside bucket",
        optional: true,
      },
      service: {
        type: "input",
        label: "Service",
        description: "Service",
        hint: "Supported services: compute, storage, bigquery, iam",
        choices: GcloudServices,
        optional: true,
      },
      resourceTag: {
        type: "object",
        label: "Resource tag",
        optional: true,
        properties: {
          tagKey: {
            type: "generated",
            label: "Tag key",
            optional: true,
          },
          tagValue: {
            type: "generated",
            label: "Tag value",
            optional: true,
          },
        },
      },
      permissionList: {
        type: "object",
        label: "Permission list",
        multivalued: true,
      },
    },
    render: {
      locator: "projectId",
      policy: "accesses",
      resource: "resourceName",
    } as const,
  }),
  role: Access({
    description: "Role within Google Cloud",
    label: "Role",
    maturity: "ga",
    delegation: toolUserDelegation,
    generated: {},
    parameters: roleParameters,
    render: { locator: "projectId", policy: "role" } as const,
  }),
  permission: Access({
    description: "Permission within Google Cloud",
    label: "Permission",
    maturity: "ga",
    delegation: toolUserDelegation,
    generated: {
      role: {
        type: "string",
      },
    },
    parameters: {
      project: projectArg,
      projectId: projectIdArg,
      "principal-type": principalTypeArg,
      name: {
        description: "Permission name or identifier",
        label: "Permissions",
        type: "dynamic",
        multivalued: true,
      },
    },
    render: { locator: "projectId", policy: "name" } as const,
  }),
};

export type GcloudAccesses = typeof GcloudAccesses;
export type GcloudAccessType = keyof GcloudAccesses;
export type GcloudAccess = GcloudAccesses[GcloudAccessType];
export type GcloudAccessArgs = ArgsOf<GcloudAccess>;

export type GcloudResourceAccess = GcloudAccesses["resource"];
export type GcloudResourceAccessArgs = ArgsOf<GcloudResourceAccess>;
export type GcloudResourceRequest = RequestOf<GcloudResourceAccess>;
export type GcloudResourceStagedRequest = StagedRequestOf<GcloudResourceAccess>;

export type GcloudPermissionAccess = GcloudAccesses["permission"];
export type GcloudPermissionAccessArgs = ArgsOf<GcloudPermissionAccess>;
export type GcloudPermissionRequest = RequestOf<GcloudPermissionAccess>;
export type GcloudPermissionStagedRequest =
  StagedRequestOf<GcloudPermissionAccess>;

export type GcloudRoleAccess = GcloudAccesses["role"];
export type GcloudRoleAccessArgs = ArgsOf<GcloudRoleAccess>;
export type GcloudRoleRequest = RequestOf<GcloudRoleAccess>;
export type GcloudRoleStagedRequest = StagedRequestOf<GcloudRoleAccess>;

export type GcloudRequest = RequestOf<GcloudAccess>;
export type GcloudStagedRequest = StagedRequestOf<GcloudAccess>;

export type GcloudAccessRequest = AccessRequestOf<GcloudAccesses>;
export type StagedGcloudAccessRequest = StagedAccessRequestOf<GcloudAccesses>;

export type GcloudPrincipalType = GcloudAccessArgs["principal-type"];
